import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { ExternalLink, ChevronDown, ChevronRight, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  type EpicWithCalculations,
  getEpicDetails,
  getClaudeAssessmentDetails,
  getEpicChildWorkItems,
  ADO_WORK_ITEM_BASE_URL,
  type EpicDetails,
  type ClaudeAssessmentDetails,
  type ChildWorkItem,
} from '../../../lib/epic-details';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../../AppContainer';
import { formatDateUK } from '../../../lib/date-utils';

interface EpicDetailModalProps {
  epic: EpicWithCalculations;
  reviewId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export function EpicDetailModal({ epic, reviewId: _reviewId, open, onOpenChange, onUpdate: _onUpdate }: EpicDetailModalProps) {
  const { apiUrl } = useProductSettings<Settings>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [epicDetails, setEpicDetails] = useState<EpicDetails | null>(null);
  const [claudeAssessment, setClaudeAssessment] = useState<ClaudeAssessmentDetails | null>(null);
  const [children, setChildren] = useState<ChildWorkItem[]>([]);

  const isFlat = epic.id.startsWith('FLAT_');

  // Section expansion states - all collapsed by default
  const [epicInfoExpanded, setEpicInfoExpanded] = useState(false);
  const [claudeAssessmentExpanded, setClaudeAssessmentExpanded] = useState(false);
  const [percentagesExpanded, setPercentagesExpanded] = useState(false);
  const [capexRationaleExpanded, setCapexRationaleExpanded] = useState(false);
  const [rndRationaleExpanded, setRndRationaleExpanded] = useState(false);
  const [capexEligibleExpanded, setCapexEligibleExpanded] = useState(false);
  const [capexNonEligibleExpanded, setCapexNonEligibleExpanded] = useState(false);
  const [rndEligibleExpanded, setRndEligibleExpanded] = useState(false);
  const [rndNonEligibleExpanded, setRndNonEligibleExpanded] = useState(false);

  // Description expansion state
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [childrenExpanded, setChildrenExpanded] = useState(false);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      if (isFlat) {
        if (!epic.project_name || !epic.fiscal_year_quarter) {
          throw new Error('Missing project_name or fiscal_year_quarter on FLAT epic — cannot load child work items');
        }

        const [details, assessment, childItems] = await Promise.all([
          getEpicDetails(apiUrl, epic.id).catch(() => null),
          getClaudeAssessmentDetails(apiUrl, epic.id).catch(() => null),
          getEpicChildWorkItems(apiUrl, epic.id, epic.project_name, epic.fiscal_year_quarter),
        ]);
        setEpicDetails(details);
        setClaudeAssessment(assessment);
        setChildren(childItems);
      } else {
        const details = await getEpicDetails(apiUrl, epic.id);
        setEpicDetails(details);

        const assessment = await getClaudeAssessmentDetails(apiUrl, epic.id);
        setClaudeAssessment(assessment);
      }

      setLoading(false);
    } catch (err) {
      console.error('ProjectApproval_ Error loading epic details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load epic details');
      setLoading(false);
    }
  }

  /* eslint-disable react-hooks/set-state-in-effect -- data fetching on modal open is intentional */
  useEffect(() => {
    if (open) {
      setEpicInfoExpanded(false);
      setClaudeAssessmentExpanded(false);
      setPercentagesExpanded(false);
      setCapexRationaleExpanded(false);
      setRndRationaleExpanded(false);
      setCapexEligibleExpanded(false);
      setCapexNonEligibleExpanded(false);
      setRndEligibleExpanded(false);
      setRndNonEligibleExpanded(false);
      setDescriptionExpanded(false);
      setChildrenExpanded(false);
      loadData();
    }
  }, [open, epic.id]);
  /* eslint-enable react-hooks/set-state-in-effect */


  const getAlmLabel = (): 'ADO' | 'JIRA' | 'ALM' => {
    if (!epicDetails?.data_source) return 'ALM';

    const lower = epicDetails.data_source.toLowerCase();

    if (lower.includes('jira')) return 'JIRA';
    return 'ADO';
  };

  // Derive active sources from epic data (read-only)
  const capexSource: 'alm' | 'claude' | 'manual' = epic.capExSource === 'M' ? 'manual' : epic.capExSource === 'C' ? 'claude' : 'alm';
  const rndSource: 'alm' | 'claude' | 'manual' = epic.rndSource === 'M' ? 'manual' : epic.rndSource === 'C' ? 'claude' : 'alm';
  const estimateSource: 'duration' | 'manual' = epic.estimateSource === 'M' ? 'manual' : 'duration';

  // Compute read-only display values
  // epic.durationDays is the raw duration from the API (days between Active and Closed dates)
  // epic.estimate is the *active* estimate which may be manual override when estimateSource === 'M'
  const durationDays = epic.durationDays;

  const hasAlmCapex = epicDetails?.capex_percentage !== null && epicDetails?.capex_percentage !== undefined;
  const hasAlmRnd = epicDetails?.r_and_d_percentage !== null && epicDetails?.r_and_d_percentage !== undefined;

  const manualEstimateValue = epic.estimateSource === 'M' ? epic.estimate : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[calc(100%-2rem)] !max-w-[900px] max-h-[90vh] !top-[calc(50%-16px)] flex flex-col overflow-hidden" showCloseButton={false} onInteractOutside={(e: Event) => e.preventDefault()} onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}>
        <DialogHeader className="sticky top-0 bg-background z-10 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <DialogTitle className="text-2xl">
                <Badge variant="outline" className="mr-2 text-base">
                  {epic.id}
                </Badge>
                {epic.title}
              </DialogTitle>
              <DialogDescription>Detailed calculation breakdown and assessment information</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-6">
              {/* Section A: EPIC Information (Header) */}
              <Collapsible open={epicInfoExpanded} onOpenChange={setEpicInfoExpanded}>
                <Card>
                  <CardHeader
                    className="cursor-pointer mx-0 my-3"
                    onClick={() => setEpicInfoExpanded(!epicInfoExpanded)}
                  >
                    <CardTitle className="flex items-center gap-2">
                      {epicInfoExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      EPIC Information
                    </CardTitle>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">State</Label>
                          <p className="text-sm mt-1">{epicDetails?.state || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Closed Date</Label>
                          <p className="text-sm mt-1">{formatDateUK(epicDetails?.closed_date || null)}</p>
                        </div>
                      </div>

                      {/* Description */}
                      {epicDetails?.description && (
                        <div className="border-t pt-4">
                          <Label className="text-sm font-semibold">Description</Label>
                          <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                            {descriptionExpanded || epicDetails.description.length <= 200
                              ? epicDetails.description
                              : `${epicDetails.description.substring(0, 200)}...`}
                          </div>
                          {epicDetails.description.length > 200 && (
                            <Button
                              variant="link"
                              className="px-0 h-auto py-1 text-sm"
                              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                            >
                              {descriptionExpanded ? 'See less' : 'See more'}
                            </Button>
                          )}
                        </div>
                      )}


                      {/* Work Item Link */}
                      <div className="border-t pt-4">
                        <Label className="text-sm font-semibold">Work Item Link</Label>
                        <a
                          href={epicDetails?.url || `${ADO_WORK_ITEM_BASE_URL}/${epic.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mt-1"
                        >
                          View in Azure DevOps
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section B: Claude Assessment Rationale (If exists) */}
              {claudeAssessment && (
                <Collapsible open={claudeAssessmentExpanded} onOpenChange={setClaudeAssessmentExpanded}>
                  <Card>
                    <CardHeader
                      className="cursor-pointer mx-0 my-3"
                      onClick={() => setClaudeAssessmentExpanded(!claudeAssessmentExpanded)}
                    >
                      <CardTitle className="flex items-center gap-2">
                        {claudeAssessmentExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                        CapEx and R&D Assessment Rationale
                      </CardTitle>
                    </CardHeader>
                    <CollapsibleContent>
                      <CardContent className="space-y-4">
                        {/* CapEx Assessment */}
                        <Collapsible open={capexRationaleExpanded} onOpenChange={setCapexRationaleExpanded}>
                          <div className="border rounded-md p-4">
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" className="w-full justify-start gap-2 px-0 hover:bg-transparent">
                                {capexRationaleExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-semibold">
                                  CapEx Assessment
                                  {claudeAssessment.capex_percentage !== null &&
                                    claudeAssessment.capex_percentage !== undefined && (
                                      <span className="ml-2 text-muted-foreground">
                                        ({claudeAssessment.capex_percentage}%)
                                      </span>
                                    )}
                                </span>
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-3 mt-3">
                              <div>
                                <Label className="text-sm font-semibold">Rationale</Label>
                                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                                  {claudeAssessment.capex_rationale}
                                </p>
                              </div>

                              {claudeAssessment.capex_eligible_work && (
                                <Collapsible open={capexEligibleExpanded} onOpenChange={setCapexEligibleExpanded}>
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="gap-2 px-0">
                                      {capexEligibleExpanded ? (
                                        <ChevronDown className="h-3 w-3" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3" />
                                      )}
                                      Eligible Work
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap pl-4">
                                      {claudeAssessment.capex_eligible_work}
                                    </p>
                                  </CollapsibleContent>
                                </Collapsible>
                              )}

                              {claudeAssessment.capex_non_eligible_work && (
                                <Collapsible open={capexNonEligibleExpanded} onOpenChange={setCapexNonEligibleExpanded}>
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="gap-2 px-0">
                                      {capexNonEligibleExpanded ? (
                                        <ChevronDown className="h-3 w-3" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3" />
                                      )}
                                      Non-Eligible Work
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap pl-4">
                                      {claudeAssessment.capex_non_eligible_work}
                                    </p>
                                  </CollapsibleContent>
                                </Collapsible>
                              )}
                            </CollapsibleContent>
                          </div>
                        </Collapsible>

                        {/* R&D Assessment */}
                        <Collapsible open={rndRationaleExpanded} onOpenChange={setRndRationaleExpanded}>
                          <div className="border rounded-md p-4">
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" className="w-full justify-start gap-2 px-0 hover:bg-transparent">
                                {rndRationaleExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-semibold">
                                  R&D Assessment
                                  {claudeAssessment.r_and_d_percentage !== null &&
                                    claudeAssessment.r_and_d_percentage !== undefined && (
                                      <span className="ml-2 text-muted-foreground">
                                        ({claudeAssessment.r_and_d_percentage}%)
                                      </span>
                                    )}
                                </span>
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-3 mt-3">
                              <div>
                                <Label className="text-sm font-semibold">Rationale</Label>
                                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                                  {claudeAssessment.r_and_d_rationale}
                                </p>
                              </div>

                              {claudeAssessment.r_and_d_eligible_work && (
                                <Collapsible open={rndEligibleExpanded} onOpenChange={setRndEligibleExpanded}>
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="gap-2 px-0">
                                      {rndEligibleExpanded ? (
                                        <ChevronDown className="h-3 w-3" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3" />
                                      )}
                                      Eligible Work
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap pl-4">
                                      {claudeAssessment.r_and_d_eligible_work}
                                    </p>
                                  </CollapsibleContent>
                                </Collapsible>
                              )}

                              {claudeAssessment.r_and_d_non_eligible_work && (
                                <Collapsible open={rndNonEligibleExpanded} onOpenChange={setRndNonEligibleExpanded}>
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="gap-2 px-0">
                                      {rndNonEligibleExpanded ? (
                                        <ChevronDown className="h-3 w-3" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3" />
                                      )}
                                      Non-Eligible Work
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap pl-4">
                                      {claudeAssessment.r_and_d_non_eligible_work}
                                    </p>
                                  </CollapsibleContent>
                                </Collapsible>
                              )}
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}

              {/* Section C: CapEx & R&D % and Estimate (Read-Only View) */}
              <Collapsible open={percentagesExpanded} onOpenChange={setPercentagesExpanded}>
                <Card>
                  <CardHeader
                    className="cursor-pointer mx-0 my-3"
                    onClick={() => setPercentagesExpanded(!percentagesExpanded)}
                  >
                    <CardTitle className="flex items-center gap-2">
                      {percentagesExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      CapEx & R&D %
                    </CardTitle>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      {/* Active Percentage Sub-section */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-semibold text-sm">Active Percentage</span>
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-xs font-normal">
                            Selected source highlighted
                          </Badge>
                        </div>
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead className="bg-muted/50">
                              <tr>
                                <th rowSpan={2} className="text-left p-3 font-medium text-sm border-r border-b min-w-[70px]">
                                  Type
                                </th>
                                <th colSpan={3} className="text-center p-3 font-medium text-sm border-b">
                                  Percentage Source
                                </th>
                              </tr>
                              <tr>
                                <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                                  {getAlmLabel()}
                                </th>
                                <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                                  System Recommended
                                </th>
                                <th className="text-center p-2 text-xs font-medium border-b bg-muted/30 min-w-[100px]">
                                  Manual Override
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* CapEx Row */}
                              <tr className="border-t">
                                <td className="p-3 border-r font-semibold text-sm">CapEx</td>
                                <td
                                  className={cn(
                                    'p-3 border-r text-center text-sm',
                                    capexSource === 'alm' && 'bg-green-100',
                                    capexSource !== 'alm' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {hasAlmCapex ? (
                                    <span className="font-semibold">{epicDetails!.capex_percentage!.toFixed(0)}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    'p-3 border-r text-center text-sm',
                                    capexSource === 'claude' && 'bg-green-100',
                                    capexSource !== 'claude' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {claudeAssessment ? (
                                    <span className="font-semibold">{claudeAssessment.capex_percentage.toFixed(0)}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    'p-3 text-center text-sm',
                                    capexSource === 'manual' && 'bg-amber-100',
                                    capexSource !== 'manual' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {epic.capExSource === 'M' ? (
                                    <span className="font-semibold">{epic.activeCapExPercentage}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">0.0</span>
                                  )}
                                </td>
                              </tr>

                              {/* R&D Row */}
                              <tr className="border-t">
                                <td className="p-3 border-r font-semibold text-sm">R&D</td>
                                <td
                                  className={cn(
                                    'p-3 border-r text-center text-sm',
                                    rndSource === 'alm' && 'bg-green-100',
                                    rndSource !== 'alm' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {hasAlmRnd ? (
                                    <span className="font-semibold">{epicDetails!.r_and_d_percentage!.toFixed(0)}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    'p-3 border-r text-center text-sm',
                                    rndSource === 'claude' && 'bg-green-100',
                                    rndSource !== 'claude' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {claudeAssessment ? (
                                    <span className="font-semibold">{claudeAssessment.r_and_d_percentage.toFixed(0)}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    'p-3 text-center text-sm',
                                    rndSource === 'manual' && 'bg-amber-100',
                                    rndSource !== 'manual' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {epic.rndSource === 'M' ? (
                                    <span className="font-semibold">{epic.activeRnDPercentage}%</span>
                                  ) : (
                                    <span className="text-muted-foreground">0.0</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Relative EPIC Size Sub-section */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-semibold text-sm">Relative EPIC Size</span>
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-xs font-normal">
                            Used in weighted average
                          </Badge>
                        </div>
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-center p-3 font-medium text-sm border-r border-b min-w-[200px]">
                                  Duration (days)
                                </th>
                                <th className="text-center p-3 font-medium text-sm border-b min-w-[200px]">
                                  Manual Override
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td
                                  className={cn(
                                    'p-3 border-r text-center text-sm',
                                    estimateSource === 'duration' && 'bg-green-100',
                                    estimateSource !== 'duration' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {durationDays > 0 ? (
                                    <span className="font-semibold">{durationDays}</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    'p-3 text-center text-sm',
                                    estimateSource === 'manual' && 'bg-amber-100',
                                    estimateSource !== 'manual' && 'hover:bg-muted/50',
                                  )}
                                >
                                  {manualEstimateValue !== null ? (
                                    <span className="font-semibold">{manualEstimateValue}</span>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Size determines this EPIC's weight in the overall average. For single-EPIC projects, the size cancels out but is still shown for record-keeping.
                        </p>
                      </div>

                      {/* Read-only info banner */}
                      <Alert className="border-blue-200 bg-blue-50 px-3 py-2">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-sm text-blue-800">
                          This is a read-only view. To change the CapEx/R&D % or Relative Epic Size values, use the Calculation Breakdown screen.
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
              {/* Section D: Included Work Items (FLAT parents only) */}
              {isFlat && children.length > 0 && (
                <Collapsible open={childrenExpanded} onOpenChange={setChildrenExpanded}>
                  <Card>
                    <CardHeader
                      className="cursor-pointer mx-0 my-3"
                      onClick={() => setChildrenExpanded(!childrenExpanded)}
                    >
                      <CardTitle className="flex items-center gap-2">
                        {childrenExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        Included Work Items
                      </CardTitle>
                    </CardHeader>
                    <CollapsibleContent>
                      <CardContent className="space-y-2">
                        {children.map((child) => (
                          <div key={child.work_item_id} className="flex items-center gap-2">
                            <a
                              href={child.url || `${ADO_WORK_ITEM_BASE_URL}/${child.work_item_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              {child.work_item_id}
                              <ExternalLink className="h-4 w-4" />
                            </a>
                            {child.title && (
                              <span className="text-sm text-muted-foreground">{child.title}</span>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          <div className="flex items-center justify-end w-full">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
